import { Pressable, StyleSheet, Text, View } from 'react-native';
import { Profile } from 'store/slices/profileSlice';
import { calcSizeForDevice, getResponsiveSize } from 'utils/index';
import { getProfileHasPin } from 'utils/pin';
import { Platform } from 'utils/platform';

import { Avatar } from './Avatar';

import { Icons } from 'components/Icons';
import { WebDiv } from 'components/WebDiv';

type ProfileCardProps =
  | { type: 'add'; onAdd: () => void }
  | { type: 'profile'; profile: Profile; onSelect: () => void; onEdit?: () => void };

export const ProfileCard: React.FC<ProfileCardProps> = (props) => {
  const isAddCard = props.type === 'add';

  const hasEditButton = props.type === 'profile' && !props?.profile?.isKid && props.onEdit;

  const hasProfilePin = () => {
    if (isAddCard) return false;
    return getProfileHasPin(props?.profile);
  };

  const getCardName = () => {
    if (isAddCard) return 'Adicionar Perfil';

    const { profile } = props;
    if (profile.isKid) return 'Modo Infantil';
    return profile.name;
  };

  const renderAvatar = () => {
    if (isAddCard) return <Avatar variant="add" onPress={props.onAdd} />;

    const { profile, onSelect } = props;
    if (profile.isKid) return <Avatar variant="kid" onPress={onSelect} />;
    return <Avatar variant="default" name={profile.name} onPress={onSelect} />;
  };

  return (
    <View style={[styles.card, Platform.isTV ? styles.tvCard : styles.webCard]}>
      {renderAvatar()}
      <View
        style={[styles.nameContent, Platform.isTV ? styles.tvNameContent : styles.webNameContent]}>
        {!isAddCard && hasProfilePin() && (
          <View style={Platform.isTV ? styles.tvLockIcon : styles.webLockIcon}>
            <Icons.Lock
              width={getResponsiveSize({ base: 16, md: 24 })}
              height={getResponsiveSize({ base: 16, md: 24 })}
            />
          </View>
        )}
        <Text
          style={[styles.name, Platform.isTV ? styles.tvName : styles.webName]}
          numberOfLines={1}
          ellipsizeMode="tail">
          {getCardName()}
        </Text>
      </View>
      {hasEditButton && (
        <WebDiv className="background-color--white">
          <Pressable
            accessibilityRole="button"
            style={[styles.editButton, Platform.isTV ? styles.tvEditButton : styles.webEditButton]}
            onPress={props.onEdit}>
            <Icons.Pencil color="#070D31" />
            <Text style={styles.buttonText}>Editar</Text>
          </Pressable>
        </WebDiv>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  card: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    marginTop: calcSizeForDevice(54, 60),
  },
  tvCard: {
    marginTop: calcSizeForDevice(60),
  },
  webCard: {
    marginTop: Number(getResponsiveSize({ base: 32, md: 54 })),
    paddingHorizontal: Number(getResponsiveSize({ base: 0, md: 32 })),
  },
  nameContent: {
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
  },
  tvNameContent: {
    maxWidth: calcSizeForDevice(252),
    marginTop: calcSizeForDevice(32),
  },
  webNameContent: {
    width: Number(getResponsiveSize({ base: 163, md: 180 })),
    marginTop: Number(getResponsiveSize({ base: 12, md: 32 })),
  },
  name: {
    fontWeight: '700',
    color: '#FFF',
    textAlign: 'center',
  },
  tvName: {
    fontSize: calcSizeForDevice(32),
  },
  webName: {
    fontSize: Number(getResponsiveSize({ base: 16, md: 24 })),
  },
  tvLockIcon: {
    marginRight: calcSizeForDevice(16),
  },
  webLockIcon: {
    marginRight: Number(getResponsiveSize({ base: 8, md: 16 })),
  },
  editButton: {
    flexDirection: 'row',
    alignItems: 'center',
    borderRadius: 8,
    backgroundColor: '#A8B4FF',
  },
  tvEditButton: {
    marginTop: calcSizeForDevice(46),
    paddingVertical: calcSizeForDevice(8),
    paddingHorizontal: calcSizeForDevice(16),
  },
  webEditButton: {
    marginTop: Number(getResponsiveSize({ base: 20, md: 38 })),
    paddingVertical: Number(getResponsiveSize({ base: 4, md: 6 })),
    paddingHorizontal: Number(getResponsiveSize({ base: 14, md: 10 })),
  },
  buttonText: {
    marginLeft: calcSizeForDevice(8),
    fontSize: calcSizeForDevice(12, 16),
    fontWeight: '700',
    textTransform: 'uppercase',
    lineHeight: calcSizeForDevice(20, 28),
    color: '#070D31',
  },
});
